// Web Audio API - Sound Effects Engine for Jarvis HUD
const AudioCtx = window.AudioContext || window.webkitAudioContext;
let audioCtx = null;

function getCtx() {
  if (!audioCtx) {
    audioCtx = new AudioCtx();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

// Play a tone with optional sweep
function playTone(freq, duration, type = 'sine', volume = 0.15, sweep = null) {
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  
  if (sweep) {
    osc.frequency.linearRampToValueAtTime(sweep, ctx.currentTime + duration);
  }
  
  gain.gain.setValueAtTime(0, ctx.currentTime);
  gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  
  osc.connect(gain);
  gain.connect(ctx.destination);
  
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + duration);
}

// Arc Reactor Boot Sequence — CINEMATIC IRON MAN POWER UP
export async function playBootSequence() {
  const ctx = getCtx();
  const now = ctx.currentTime;
  
  // === PHASE 1: Deep rumble + sub bass (0-1.5s) ===
  // Low sub-bass rumble building
  const sub = ctx.createOscillator();
  const subGain = ctx.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(30, now);
  sub.frequency.linearRampToValueAtTime(55, now + 1.5);
  subGain.gain.setValueAtTime(0, now);
  subGain.gain.linearRampToValueAtTime(0.4, now + 0.5);
  subGain.gain.linearRampToValueAtTime(0.3, now + 1.5);
  subGain.gain.linearRampToValueAtTime(0, now + 3);
  sub.connect(subGain);
  subGain.connect(ctx.destination);
  sub.start(now);
  sub.stop(now + 3);

  // Rumble noise layer
  const noiseLen = ctx.sampleRate * 3;
  const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
  const noiseData = noiseBuf.getChannelData(0);
  for (let i = 0; i < noiseLen; i++) noiseData[i] = (Math.random() * 2 - 1) * 0.3;
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  const noiseFilter = ctx.createBiquadFilter();
  noiseFilter.type = 'lowpass';
  noiseFilter.frequency.setValueAtTime(80, now);
  noiseFilter.frequency.linearRampToValueAtTime(400, now + 2);
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0, now);
  noiseGain.gain.linearRampToValueAtTime(0.15, now + 0.3);
  noiseGain.gain.linearRampToValueAtTime(0.08, now + 1.5);
  noiseGain.gain.linearRampToValueAtTime(0, now + 3);
  noise.connect(noiseFilter);
  noiseFilter.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noise.start(now);
  noise.stop(now + 3);

  // === PHASE 2: Rising power sweep (0.5-2.5s) ===
  const sweep = ctx.createOscillator();
  const sweepGain = ctx.createGain();
  sweep.type = 'sawtooth';
  sweep.frequency.setValueAtTime(80, now + 0.5);
  sweep.frequency.exponentialRampToValueAtTime(2000, now + 2.5);
  sweepGain.gain.setValueAtTime(0, now + 0.5);
  sweepGain.gain.linearRampToValueAtTime(0.06, now + 1);
  sweepGain.gain.linearRampToValueAtTime(0.03, now + 2);
  sweepGain.gain.linearRampToValueAtTime(0, now + 2.5);
  sweep.connect(sweepGain);
  sweepGain.connect(ctx.destination);
  sweep.start(now + 0.5);
  sweep.stop(now + 2.5);

  // === PHASE 3: Power-up tones ascending (1-2.5s) ===
  const notes = [130.8, 164.8, 196, 246.9, 329.6, 440, 523.3];
  notes.forEach((freq, i) => {
    const t = now + 1 + i * 0.18;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(0.1, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.3);
  });

  // === PHASE 4: Arc reactor IGNITION (2.3s) ===
  const ignite = ctx.createOscillator();
  const igniteGain = ctx.createGain();
  ignite.type = 'sine';
  ignite.frequency.setValueAtTime(220, now + 2.3);
  ignite.frequency.linearRampToValueAtTime(180, now + 3.5);
  igniteGain.gain.setValueAtTime(0, now + 2.3);
  igniteGain.gain.linearRampToValueAtTime(0.2, now + 2.35);
  igniteGain.gain.exponentialRampToValueAtTime(0.05, now + 3);
  igniteGain.gain.linearRampToValueAtTime(0, now + 3.8);
  ignite.connect(igniteGain);
  igniteGain.connect(ctx.destination);
  ignite.start(now + 2.3);
  ignite.stop(now + 3.8);

  // Ignition "thump"
  const thump = ctx.createOscillator();
  const thumpGain = ctx.createGain();
  thump.type = 'sine';
  thump.frequency.setValueAtTime(60, now + 2.3);
  thump.frequency.exponentialRampToValueAtTime(20, now + 2.8);
  thumpGain.gain.setValueAtTime(0.5, now + 2.3);
  thumpGain.gain.exponentialRampToValueAtTime(0.001, now + 2.8);
  thump.connect(thumpGain);
  thumpGain.connect(ctx.destination);
  thump.start(now + 2.3);
  thump.stop(now + 2.8);

  // === PHASE 5: "Online" confirmation chord (2.8-4s) ===
  const chordFreqs = [261.6, 329.6, 392, 523.3];
  chordFreqs.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now + 2.8);
    g.gain.setValueAtTime(0, now + 2.8);
    g.gain.linearRampToValueAtTime(0.06, now + 2.9);
    g.gain.exponentialRampToValueAtTime(0.001, now + 4.5);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(now + 2.8);
    osc.stop(now + 4.5);
  });

  // High-freq "ding" confirmation
  const ding = ctx.createOscillator();
  const dingGain = ctx.createGain();
  ding.type = 'sine';
  ding.frequency.setValueAtTime(1568, now + 3);
  dingGain.gain.setValueAtTime(0.08, now + 3);
  dingGain.gain.exponentialRampToValueAtTime(0.001, now + 3.8);
  ding.connect(dingGain);
  dingGain.connect(ctx.destination);
  ding.start(now + 3);
  ding.stop(now + 3.8);

  // === PHASE 6: Sustained reactor hum (fades into ambient) ===
  const hum = ctx.createOscillator();
  const humGain = ctx.createGain();
  hum.type = 'sine';
  hum.frequency.setValueAtTime(120, now + 3);
  humGain.gain.setValueAtTime(0, now + 3);
  humGain.gain.linearRampToValueAtTime(0.04, now + 3.5);
  humGain.gain.linearRampToValueAtTime(0, now + 5);
  hum.connect(humGain);
  humGain.connect(ctx.destination);
  hum.start(now + 3);
  hum.stop(now + 5);

  // Wait for sequence to complete
  await sleep(4500);
}

// Mic activation chirp
export function playMicOn() {
  playTone(660, 0.08, 'sine', 0.1);
  setTimeout(() => playTone(880, 0.1, 'sine', 0.1), 60);
}

// Mic deactivation
export function playMicOff() {
  playTone(880, 0.08, 'sine', 0.08);
  setTimeout(() => playTone(660, 0.1, 'sine', 0.08), 60);
}

// Thinking indicator
export function playThinking() {
  playTone(440, 0.15, 'triangle', 0.06);
}

// Response received
export function playResponseReady() {
  playTone(523, 0.1, 'sine', 0.08);
  setTimeout(() => playTone(659, 0.12, 'sine', 0.08), 80);
}

// Error sound
export function playError() {
  playTone(200, 0.3, 'sawtooth', 0.08);
  setTimeout(() => playTone(150, 0.4, 'sawtooth', 0.06), 150);
}

// State change click
export function playStateChange() {
  playTone(1200, 0.05, 'sine', 0.06);
}

// Play base64 audio (for ElevenLabs responses)
// Global audio tracker — ensures only ONE audio plays at a time
let currentAudioEl = null;
let allActiveAudios = [];

function trackAudio(audio) {
  // Stop ALL other audio before playing new one
  allActiveAudios.forEach(a => {
    try { a.pause(); a.currentTime = 0; } catch(e) {}
  });
  allActiveAudios = [audio];
  currentAudioEl = audio;
  audio.addEventListener('ended', () => {
    allActiveAudios = allActiveAudios.filter(a => a !== audio);
    if (currentAudioEl === audio) currentAudioEl = null;
  });
}

export function playBase64Audio(base64Data) {
  return new Promise((resolve, reject) => {
    try {
      duckAmbient(true);
      
      const audio = new Audio(`data:audio/mp3;base64,${base64Data}`);
      trackAudio(audio); // Stop all other audio, track this one
      
      audio.onended = () => {
        currentAudioEl = null;
        duckAmbient(false);
        resolve();
      };
      audio.onerror = (e) => {
        currentAudioEl = null;
        duckAmbient(false);
        reject(e);
      };
      audio.play().catch((e) => {
        currentAudioEl = null;
        duckAmbient(false);
        reject(e);
      });
    } catch (e) {
      duckAmbient(false);
      reject(e);
    }
  });
}

export function stopCurrentAudio() {
  allActiveAudios.forEach(a => {
    try { a.pause(); a.currentTime = 0; } catch(e) {}
  });
  allActiveAudios = [];
  currentAudioEl = null;
  duckAmbient(false);
}

// ========================================
// AMBIENT BACKGROUND MUSIC ENGINE
// Iron Man Lab Atmosphere — CINEMATIC
// ========================================
let ambientNodes = [];
let ambientMasterGain = null;
let ambientRunning = false;

export function startAmbient() {
  if (ambientRunning) return;
  const ctx = getCtx();
  ambientRunning = true;
  const now = ctx.currentTime;
  
  ambientMasterGain = ctx.createGain();
  ambientMasterGain.gain.setValueAtTime(0, now);
  ambientMasterGain.gain.linearRampToValueAtTime(0.18, now + 4);
  ambientMasterGain.connect(ctx.destination);
  
  // === Layer 1: Sub bass foundation (40Hz) ===
  const sub = ctx.createOscillator();
  const subG = ctx.createGain();
  sub.type = 'sine';
  sub.frequency.setValueAtTime(40, now);
  subG.gain.setValueAtTime(0.35, now);
  sub.connect(subG);
  subG.connect(ambientMasterGain);
  sub.start();
  ambientNodes.push(sub);
  
  // === Layer 2: Reactor hum with wobble (80Hz) ===
  const hum = ctx.createOscillator();
  const humG = ctx.createGain();
  hum.type = 'sine';
  hum.frequency.setValueAtTime(80, now);
  const humLfo = ctx.createOscillator();
  const humLfoG = ctx.createGain();
  humLfo.type = 'sine';
  humLfo.frequency.setValueAtTime(0.12, now);
  humLfoG.gain.setValueAtTime(2, now);
  humLfo.connect(humLfoG);
  humLfoG.connect(hum.frequency);
  humLfo.start();
  humG.gain.setValueAtTime(0.25, now);
  hum.connect(humG);
  humG.connect(ambientMasterGain);
  hum.start();
  ambientNodes.push(hum, humLfo);
  
  // === Layer 3: Harmonic bed (C2 + E2 + G2 chord drone) ===
  [130.8, 164.8, 196].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, now);
    // Subtle detuning for warmth
    const detune = ctx.createOscillator();
    const detuneG = ctx.createGain();
    detune.type = 'sine';
    detune.frequency.setValueAtTime(0.05 + i * 0.02, now);
    detuneG.gain.setValueAtTime(1.5, now);
    detune.connect(detuneG);
    detuneG.connect(osc.frequency);
    detune.start();
    g.gain.setValueAtTime(0.04, now);
    osc.connect(g);
    g.connect(ambientMasterGain);
    osc.start();
    ambientNodes.push(osc, detune);
  });
  
  // === Layer 4: High shimmer pad (slowly evolving) ===
  const shimmer = ctx.createOscillator();
  const shimG = ctx.createGain();
  shimmer.type = 'sine';
  shimmer.frequency.setValueAtTime(523, now);
  const shimLfo = ctx.createOscillator();
  const shimLfoG = ctx.createGain();
  shimLfo.type = 'sine';
  shimLfo.frequency.setValueAtTime(0.04, now);
  shimLfoG.gain.setValueAtTime(8, now);
  shimLfo.connect(shimLfoG);
  shimLfoG.connect(shimmer.frequency);
  shimLfo.start();
  shimG.gain.setValueAtTime(0.02, now);
  shimmer.connect(shimG);
  shimG.connect(ambientMasterGain);
  shimmer.start();
  ambientNodes.push(shimmer, shimLfo);
  
  // === Layer 5: Rhythmic pulse (subtle heartbeat, 70 BPM) ===
  const pulseInterval = 60 / 70; // seconds per beat
  function createPulse() {
    const scheduleAhead = 10; // schedule 10 seconds ahead
    let beatTime = ctx.currentTime;
    
    function scheduleBeat() {
      while (beatTime < ctx.currentTime + scheduleAhead) {
        // Low thump
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(55, beatTime);
        osc.frequency.exponentialRampToValueAtTime(30, beatTime + 0.15);
        g.gain.setValueAtTime(0.12, beatTime);
        g.gain.exponentialRampToValueAtTime(0.001, beatTime + 0.2);
        osc.connect(g);
        g.connect(ambientMasterGain);
        osc.start(beatTime);
        osc.stop(beatTime + 0.25);
        
        // Subtle click on every other beat
        if (Math.round(beatTime / pulseInterval) % 2 === 0) {
          const click = ctx.createOscillator();
          const cg = ctx.createGain();
          click.type = 'sine';
          click.frequency.setValueAtTime(800, beatTime);
          cg.gain.setValueAtTime(0.015, beatTime);
          cg.gain.exponentialRampToValueAtTime(0.001, beatTime + 0.05);
          click.connect(cg);
          cg.connect(ambientMasterGain);
          click.start(beatTime);
          click.stop(beatTime + 0.06);
        }
        
        beatTime += pulseInterval;
      }
      
      if (ambientRunning) {
        setTimeout(scheduleBeat, (scheduleAhead * 1000) / 2);
      }
    }
    scheduleBeat();
  }
  createPulse();
  
  // === Layer 6: Filtered noise texture (lab air + electronics) ===
  const bufLen = ctx.sampleRate * 4;
  const noiseBuf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
  const noiseData = noiseBuf.getChannelData(0);
  for (let i = 0; i < bufLen; i++) noiseData[i] = (Math.random() * 2 - 1);
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuf;
  noise.loop = true;
  const nFilt = ctx.createBiquadFilter();
  nFilt.type = 'bandpass';
  nFilt.frequency.setValueAtTime(300, now);
  nFilt.Q.setValueAtTime(0.8, now);
  // Slowly sweep the filter
  const nFiltLfo = ctx.createOscillator();
  const nFiltLfoG = ctx.createGain();
  nFiltLfo.type = 'sine';
  nFiltLfo.frequency.setValueAtTime(0.02, now);
  nFiltLfoG.gain.setValueAtTime(150, now);
  nFiltLfo.connect(nFiltLfoG);
  nFiltLfoG.connect(nFilt.frequency);
  nFiltLfo.start();
  const nG = ctx.createGain();
  nG.gain.setValueAtTime(0.06, now);
  noise.connect(nFilt);
  nFilt.connect(nG);
  nG.connect(ambientMasterGain);
  noise.start();
  ambientNodes.push(noise, nFiltLfo);
  
  // === Layer 7: Occasional electronic chirps (random, subtle) ===
  function scheduleChirps() {
    if (!ambientRunning) return;
    const delay = 3 + Math.random() * 8; // 3-11 seconds between chirps
    setTimeout(() => {
      if (!ambientRunning) return;
      const c = getCtx();
      const t = c.currentTime;
      const freq = 800 + Math.random() * 1500;
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, t);
      osc.frequency.linearRampToValueAtTime(freq * (0.8 + Math.random() * 0.4), t + 0.1);
      g.gain.setValueAtTime(0.02, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      osc.connect(g);
      g.connect(ambientMasterGain);
      osc.start(t);
      osc.stop(t + 0.2);
      scheduleChirps();
    }, delay * 1000);
  }
  scheduleChirps();
}

function duckAmbient(duck) {
  if (!ambientMasterGain) return;
  const ctx = getCtx();
  const target = duck ? 0.04 : 0.18;
  ambientMasterGain.gain.cancelScheduledValues(ctx.currentTime);
  ambientMasterGain.gain.linearRampToValueAtTime(target, ctx.currentTime + 0.4);
}

export function stopAmbient() {
  ambientNodes.forEach(node => {
    try { node.stop(); } catch(e) {}
  });
  ambientNodes = [];
  if (ambientMasterGain) {
    try { ambientMasterGain.disconnect(); } catch(e) {}
    ambientMasterGain = null;
  }
  ambientRunning = false;
}

export function isAmbientRunning() {
  return ambientRunning;
}

// Resume audio context (needed after user interaction)
export function resumeAudio() {
  getCtx();
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
